import React, { useEffect, useMemo, useState } from "react";
import ApiServices from "../../services/apiServices";
import "./index.css";
import { useLoad } from "../../context/Load";
import Tabela from "../../Components/Tabela";
import { BsFiletypeXlsx } from "react-icons/bs";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

export default function RankingPreVenda() {
  const { setLoad } = useLoad();
  const [carregando, setCarregando] = useState(false);
  const [pesquisar, setPesquisar] = useState("");
  const [grupoSelecionado, setGrupoSelecionado] = useState("Todos");
  const [tipoRanking, setTipoRanking] = useState("vendedor");
  const [listaRanking, setListaRanking] = useState([]);
  const [filtroColuna, setFiltroColuna] = useState([]);
  const [paginacao, setPaginacao] = useState({ pageIndex: 0, pageSize: 10 });

  // Modal de chassis (carregados sob demanda para otimizar a tela).
  const [chassiModalAberto, setChassiModalAberto] = useState(false);
  const [chassiCarregando, setChassiCarregando] = useState(false);
  const [chassiTitulo, setChassiTitulo] = useState("");
  const [chassiLista, setChassiLista] = useState([]);

  const buscarRanking = async () => {
    setCarregando(true);
    try {
      const visao = tipoRanking === "gerente" ? "gerentes" : "global";
      const response = await ApiServices.apiGet(
        `importacao/campanha-pre-venda/ranking?visao=${visao}`
      );
      setListaRanking(response.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    buscarRanking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoRanking]);

  async function abrirChassis(row) {
    const visaoConcessionaria = tipoRanking === "gerente";
    const titulo = visaoConcessionaria
      ? `Chassis — ${row.dealer || row.dealerCode || "Concessionária"}`
      : `Chassis — ${row.nome || row.cpf || "Vendedor"}${
          row.ehGerente && row.tierGroup != null ? ` (Grupo ${row.tierGroup})` : ""
        }`;

    setChassiTitulo(titulo);
    setChassiLista([]);
    setChassiModalAberto(true);

    // Gerente é listado por grupo: detalhar só os chassis daquele grupo.
    const grupoParam =
      !visaoConcessionaria && row.ehGerente && row.tierGroup != null
        ? `&grupo=${row.tierGroup}`
        : "";

    // Compatibilidade: se o ranking já trouxe os chassis inline (API atual da VLD),
    // usa direto — sem chamada extra.
    if (row.chassis && String(row.chassis).trim()) {
      const lista = String(row.chassis)
        .split(",")
        .map((c) => ({ chassi: c.trim() }))
        .filter((x) => x.chassi);
      setChassiLista(lista);
      setChassiCarregando(false);
      return;
    }

    // Caso contrário, busca sob demanda no endpoint otimizado (API nova).
    const visao = visaoConcessionaria ? "gerentes" : "global";
    const chave = visaoConcessionaria ? row.dealerCode : row.cpf;
    setChassiCarregando(true);
    try {
      const response = await ApiServices.apiGet(
        `importacao/campanha-pre-venda/chassis?visao=${visao}&chave=${encodeURIComponent(
          chave || ""
        )}${grupoParam}`
      );
      setChassiLista(response.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setChassiCarregando(false);
    }
  }

  const dadosFiltrados = useMemo(() => {
    const parsePosicao = (valor) => {
      const numero = Number(valor);
      return Number.isFinite(numero) ? numero : Infinity;
    };
    // Visão Vendedor exibe somente vendedores (oculta gerentes).
    const somenteVendedores = tipoRanking !== "gerente";
    return (listaRanking || [])
      .filter((item) => {
        if (somenteVendedores && item.ehGerente) return false;
        const grupoOk =
          grupoSelecionado === "Todos" ||
          String(item.tierGroup) === String(grupoSelecionado);
        if (!grupoOk) return false;
        if (!pesquisar.trim()) return true;
        const termo = pesquisar.toLowerCase();
        return (
          item.nome?.toLowerCase().includes(termo) ||
          item.dealer?.toLowerCase().includes(termo) ||
          item.cpf?.includes(termo) ||
          item.dealerCode?.includes(termo) ||
          String(item.posicao).includes(termo) ||
          String(item.pontos).includes(termo)
        );
      })
      // Sempre ordenar pela posição, do menor para o maior.
      .sort((a, b) => parsePosicao(a.posicao) - parsePosicao(b.posicao));
  }, [listaRanking, pesquisar, grupoSelecionado, tipoRanking]);

  const dadosFinais = useMemo(() => {
    const inicio = paginacao.pageIndex * paginacao.pageSize;
    return dadosFiltrados.slice(inicio, inicio + paginacao.pageSize);
  }, [dadosFiltrados, paginacao]);

  const colunas = useMemo(() => {
    const base = [
      {
        accessorKey: "posicao",
        header: "Posição",
        size: 90,
        Cell: ({ row }) =>
          row.original.ehGerente ? (
            <span style={{ color: "#888" }}>—</span>
          ) : (
            row.original.posicao
          ),
      },
      { accessorKey: "tierGroup", header: "Grupo", size: 70 },
      {
        accessorKey: "cargo",
        header: "Cargo",
        size: 110,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original.ehGerente ? (
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#0d6efd",
                border: "1px solid #0d6efd",
                borderRadius: 4,
                padding: "1px 6px",
              }}
            >
              Gerente
            </span>
          ) : (
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#198754",
                border: "1px solid #198754",
                borderRadius: 4,
                padding: "1px 6px",
              }}
            >
              Vendedor
            </span>
          ),
      },
      { accessorKey: "nome", header: "Nome", size: 220 },
      { accessorKey: "cpf", header: "CPF", size: 120 },
      { accessorKey: "dealer", header: "Concessionária", size: 220 },
      { accessorKey: "dealerCode", header: "Dealer Code", size: 100 },
      { accessorKey: "pontos", header: "Qtde", size: 70 },
      {
        accessorKey: "chassis",
        header: "Chassis",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            style={{ fontSize: 12, whiteSpace: "nowrap" }}
            onClick={() => abrirChassis(row.original)}
          >
            Detalhes (ver chassis)
          </button>
        ),
      },
    ];

    const porChave = Object.fromEntries(base.map((c) => [c.accessorKey, c]));

    // Na visão de Concessionária, ocultar Nome, CPF e Cargo (mostra a loja) e
    // deixar Chassis como última coluna, logo à direita de Dealer Code.
    if (tipoRanking === "gerente") {
      return [
        "posicao",
        "tierGroup",
        "dealer",
        "dealerCode",
        "pontos",
        "chassis",
      ].map((chave) => porChave[chave]);
    }
    // Na visão Vendedor, ocultar Concessionária e Dealer Code
    // (essa informação fica no detalhe de chassis).
    return base.filter(
      (c) => c.accessorKey !== "dealer" && c.accessorKey !== "dealerCode"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoRanking]);

  async function exportar() {
    setLoad(true);
    try {
      const visao = tipoRanking === "gerente" ? "gerentes" : "global";
      await ApiServices.apiDownloadXlsxGET(
        `importacao/campanha-pre-venda/rankingxls?visao=${visao}`,
        "ranking Novo Hyundai i20"
      );
    } finally {
      setLoad(false);
    }
  }

  return (
    <div className="row p-3">
      <span className="tituloPagina">RANKING NOVO HYUNDAI I20</span>

      <div className="col-md-3 mt-3">
        <label>Ranking</label>
        <select
          className="form-select"
          value={tipoRanking}
          onChange={(e) => setTipoRanking(e.target.value)}
        >
          <option value="vendedor">Vendedor</option>
          <option value="gerente">Concessionária</option>
        </select>
      </div>

      <div className="col-md-3 mt-3">
        <label>Grupo (TierGroup)</label>
        <select
          className="form-select"
          value={grupoSelecionado}
          onChange={(e) => setGrupoSelecionado(e.target.value)}
        >
          <option value="Todos">Todos</option>
          <option value="1">Grupo 1</option>
          <option value="2">Grupo 2</option>
          <option value="3">Grupo 3</option>
          <option value="4">Grupo 4</option>
        </select>
      </div>

      <div className="col-md-4 mt-3">
        <label>Pesquisar</label>
        <input
          className="form-control"
          value={pesquisar}
          onChange={(e) => setPesquisar(e.target.value)}
        />
      </div>

      <div className="col-md-2 mt-3 d-flex align-items-end">
        <button className="btn btn-primary w-100" onClick={exportar}>
          <BsFiletypeXlsx /> Exportar
        </button>
      </div>

      <div className="col-12 mt-4">
        <Tabela
          key={tipoRanking}
          colunas={colunas}
          dados={dadosFinais}
          totalLinhas={dadosFiltrados.length}
          paginacao={paginacao}
          setPaginacao={setPaginacao}
          filtroColuna={filtroColuna}
          setFiltroColuna={setFiltroColuna}
          buscaGlobal={false}
          buscaColunas={false}
          paginacaoPelaApi={true}
        />
      </div>

      <Dialog
        open={chassiModalAberto}
        onClose={() => setChassiModalAberto(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{chassiTitulo}</DialogTitle>
        <DialogContent dividers>
          {chassiCarregando ? (
            <p>Carregando chassis...</p>
          ) : chassiLista.length === 0 ? (
            <p>Nenhum chassi encontrado.</p>
          ) : (
            <table className="table table-sm table-striped mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Chassi</th>
                  <th>Concessionária</th>
                  <th>Vendedor</th>
                  <th>Data/Hora</th>
                </tr>
              </thead>
              <tbody>
                {chassiLista.map((c, i) => (
                  <tr key={`${c.chassi}-${i}`}>
                    <td>{i + 1}</td>
                    <td>{c.chassi}</td>
                    <td>{c.dealer || c.dealerCode || "-"}</td>
                    <td>{c.nomeVendedor || c.cpf || "-"}</td>
                    <td>{c.dataHoraVenda || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChassiModalAberto(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
